import React, { useState, useCallback } from 'react';
import {
  Typography,
  Button,
  Card,
  List,
  Tag,
  message,
  Spin,
  Grid,
  Select,
  Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, HeartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { News, NewsCategory } from '@/types/community/news';
import NewsModal from '@/components/features/community/NewsModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { newsItems as mockNews } from '@/mock/en/community/news';


const { Paragraph } = Typography;

const { Option } = Select;
const { useBreakpoint } = Grid;

// --- ARTICLE TYPES ---
const articleTypes: Array<{ key: NewsCategory; labelKey: string; color: string }> = [
  { key: 'event', labelKey: 'types.event', color: 'green' },
  { key: 'alert', labelKey: 'types.alert', color: 'red' },
  { key: 'incident', labelKey: 'types.incident', color: 'orange' },
  { key: 'guideline', labelKey: 'types.guideline', color: 'purple' },
];

const NewsPage: React.FC = () => {
  const { t } = useTranslation(['news', 'common']);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedNews, setSelectedNews] = useState<News | null>(null);
  const [deleteError, setDeleteError] = useState<Error | undefined>(undefined);
  const [searchTerm] = useState('');
  const [filterType, setFilterType] = useState<NewsCategory | undefined>(undefined);

  // Use mock data instead of API calls
  const { data: newsData, isLoading } = useQuery({
    queryKey: ['news', 'mock'],
    queryFn: async () => {
      const news = await mockNews;
      return { news };
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const deleteNewsMutation = useMutation({
    mutationFn: async () => {
      // Simulate API call for mock data
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true };
    },
    onSuccess: () => {
      message.success(t('news:newsDeleted'));
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setIsDeleteModalVisible(false);
      setSelectedNews(null);
      setDeleteError(undefined);
    },
    onError: (error: any) => {
      setDeleteError(error);
    }
  });

  const likeMutation = useMutation({
    mutationFn: async () => {
      // Simulate API call for mock data
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
    },
    onError: (error: any) => {
      console.error('Error liking news:', error);
      message.error('Failed to like news');
    }
  });

  const newsList = newsData?.news || [];

  const handleAddNews = useCallback(() => {
    setSelectedNews(null);
    setIsModalOpen(true);
  }, []);

  const handleEditNews = useCallback((news: News) => {
    setSelectedNews(news);
    setIsModalOpen(true);
  }, []);

  const handleViewNews = useCallback((news: News) => {
    navigate(`/community/news/${news.id}`, { 
      state: { 
        news,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleDeleteNews = useCallback((news: News) => {
    setSelectedNews(news);
    setIsDeleteModalVisible(true);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedNews(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setSelectedNews(null);
  }, []);

  const handleLikeNews = useCallback(() => {
    likeMutation.mutate();
  }, [likeMutation]);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedNews) {
      deleteNewsMutation.mutate();
    }
  }, [selectedNews, deleteNewsMutation]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
    setSelectedNews(null);
  }, []);

  const filteredNews = newsList.filter(news => {
    const typeMatch = filterType ? news.category === filterType : true;
    const searchTermMatch = searchTerm 
      ? news.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        news.description.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    return typeMatch && searchTermMatch;
  });

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6" style={{ marginBottom: '24px' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNews}>
          {t('news:addNews')}
        </Button>
        <Select
          placeholder={t('news:filterByType')}
          allowClear
          value={filterType}
          onChange={setFilterType}
          style={{ marginLeft: '16px' }}
        >
          {articleTypes.map(type => (
            <Option key={type.key} value={type.key}>
              {t(type.labelKey)}
            </Option>
          ))}
        </Select>
      </div>

      {/* News Feed Section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ 
          width: screens.xs ? '100%' : '500px',
          maxWidth: '100%'
        }}>
          <List
            dataSource={filteredNews}
            renderItem={news => (
              <List.Item style={{ padding: '0 0 24px 0' }}>
                <Card
                  hoverable
                  styles={{ body: { padding: 0 } }}
                  style={{ width: '100%' }}
                >
                  {/* Author Section - Instagram Style */}
                  <div style={{ padding: '16px 16px 0 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {news.author?.profilePicture?.url ? (
                          <img
                            src={news.author.profilePicture.url}
                            alt={news.author?.name || 'Author'}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              marginRight: 12,
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: 12,
                            color: '#666',
                            fontWeight: 'bold'
                          }}>
                            {news.author?.name ? news.author.name.charAt(0).toUpperCase() : '?'}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px' }}>{news.author?.name || 'Unknown Author'}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {news.author?.type ? news.author.type.charAt(0).toUpperCase() + news.author.type.slice(1) : 'Unknown'}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {dayjs(news.publishDate).format('MMM DD, YYYY')}
                      </div>
                    </div>
                  </div>

                  {/* Content Image */}
                  {(news.photos?.[0]?.url || news.attachments?.[0]?.url) && (
                    <img
                      alt={news.title}
                      src={news.photos?.[0]?.url || news.attachments?.[0]?.url}
                      style={{ 
                        width: '100%',
                        height: 250, 
                        objectFit: 'cover'
                      }}
                    />
                  )}

                  {/* Content Section */}
                  <div style={{ padding: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Typography.Title level={5} style={{ margin: 0 }}>{news.title}</Typography.Title>
                    </div>
                    
                    <Paragraph ellipsis={{ rows: 3 }} style={{ marginBottom: '12px' }}>
                      {news.description}
                    </Paragraph>

                    {/* Actions Row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span 
                          style={{ cursor: 'pointer', color: '#e91e63', display: 'flex', alignItems: 'center', gap: '4px' }}
                          onClick={handleLikeNews}
                        >
                          <HeartOutlined style={{ fontSize: '16px' }} />
                          <span style={{ fontSize: '14px' }}>{news.liked?.count || 0}</span>
                        </span>
                        <Tag>{news.category}</Tag>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Tooltip title={t('common:actions.view')}>
                          <EyeOutlined 
                            onClick={() => handleViewNews(news)}
                            style={{ fontSize: '16px', color: '#666', cursor: 'pointer' }}
                          />
                        </Tooltip>
                        <Tooltip title={t('common:actions.edit')}>
                          <EditOutlined 
                            onClick={() => handleEditNews(news)}
                            style={{ fontSize: '16px', color: '#666', cursor: 'pointer' }}
                          />
                        </Tooltip>
                        <Tooltip title={t('common:actions.delete')}>
                          <DeleteOutlined 
                            onClick={() => handleDeleteNews(news)}
                            style={{ fontSize: '16px', color: '#666', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
        </div>
      </div>

      {/* News Modal */}
      <NewsModal
        open={isModalOpen}
        news={selectedNews}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('news:deleteConfirm')}
        error={deleteError}
      />
    </div>
  );
};

export default NewsPage; 